/* eslint-disable padding-line-between-statements, @typescript-eslint/no-use-before-define */
import { useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { KYC_DOCUMENTS_TYPES } from '@shinetools/kyc-validation';

import asBentoModule from 'common/bento/hoc/asBentoModule';
import withStateMachine from 'common/bento/hoc/withStateMachine/withStateMachine';
import assertNever from 'common/bento/lib/assertNever';
import stringifyState from 'common/bento/lib/stringifyState';
import SuspenseQuery from 'components/SuspenseQuery';

import OBLoaderPage from '../../components/OBLoaderPage/OBLoaderPage';
import { ProofOfAddressModuleDataDocument } from './graphql/queries/proofOfAddressModuleData.gql';
import machineConfig, { model, State } from './machine';
import AddressFormPage from './pages/AddressFormPage';
import HostedHome from './pages/HostedHome';
import HostId from './pages/HostId';
import ProofOfAccommodation from './pages/ProofOfAccommodation';
import ProofOfAddressChecklist from './pages/ProofOfAddressChecklist';
import ProofOfAddressSelectType from './pages/ProofOfAddressSelectType';
import ProofOfAddressUpload from './pages/ProofOfAddressUpload';
import ReviewAddressAndPoa from './pages/ReviewAddressAndPoa';
import SelectAccomodationType from './pages/SelectAccommodationType';

interface ProofOfAddressProps {
  companyProfileId: string;
  isShineStart: boolean;
}

/**
 * This module is responsible to retrieve the personal address of the user and the supporting document.
 *
 * @input A user whose address is registered or not and with or without an uploaded document.
 * @output A user whose address is registered and backed by a proof of address document.
 */
const ProofOfAddress = asBentoModule<ProofOfAddressProps>(
  ({ companyProfileId, isShineStart, onDone }) => {
    const query = useQuery(ProofOfAddressModuleDataDocument, {
      variables: {
        companyProfileId,
        kycDocumentTypes: KYC_DOCUMENTS_TYPES,
      },
    });

    return (
      <SuspenseQuery
        loader={<OBLoaderPage onPrev={() => null} />}
        query={query}
      >
        {(data) => (
          <ProofOfAddressComponent
            moduleData={{
              ...data,
              isShineStart,
            }}
            onDone={onDone}
          />
        )}
      </SuspenseQuery>
    );
  },
);

const ProofOfAddressComponent = withStateMachine<typeof model>(
  ({ machine, moduleData }) => {
    const [state, send] = machine;

    const stringifiedState = stringifyState<State>(state);

    const onPrev = useCallback(() => send(model.events.PREV()), [send]);

    switch (stringifiedState) {
      case State.ADDRESS_FORM:
      case State.ADDRESS_SEND:
        return (
          <AddressFormPage
            initialValues={state.context.address}
            isSubmitting={state.matches(State.ADDRESS_SEND)}
            onPrev={onPrev}
            onSubmit={(values) => send(model.events.SUBMIT_ADDRESS(values))}
          />
        );

      case State.ACCOMMODATION_TYPE:
        return (
          <SelectAccomodationType
            address={state.context.address}
            isLoading={false}
            onEditAddress={() => send(model.events.EDIT_ADDRESS())}
            onHosted={() => send(model.events.GO_HOSTED_HOME())}
            onPrev={onPrev}
            onSelfHosted={() => send(model.events.GO_POA())}
          />
        );

      case State.HOSTED_HOME:
        return (
          <HostedHome
            moduleData={moduleData}
            onAccomodation={() => send(model.events.GO_HOSTED_ACCOMMODATION())}
            onContinue={() => send(model.events.NEXT())}
            onHostIdClick={() => send(model.events.GO_HOSTED_HOST_ID())}
            onPoa={() => send(model.events.GO_HOSTED_POA())}
            onPrev={onPrev}
            onSelfHostedClick={() => send(model.events.GO_POA())}
          />
        );
      case State.HOSTED_HOST_ID:
      case State.HOSTED_HOST_ID_SEND:
      case State.HOSTED_HOST_ID_DELETE:
        return (
          <HostId
            isLoading={[
              State.HOSTED_HOST_ID_DELETE,
              State.HOSTED_HOST_ID_SEND,
            ].some(state.matches)}
            isUploaderLoading={state.matches(State.HOSTED_HOST_ID_SEND)}
            moduleData={moduleData}
            onContinue={() => send(model.events.NEXT())}
            onDeleteFileClick={(kycDocumentId) =>
              send(model.events.DELETE_DOCUMENT_FILE(kycDocumentId))
            }
            onPrev={onPrev}
            onSuccess={(document) => {
              send(model.events.SUBMIT_DOCUMENT_FILE(document));
            }}
          />
        );

      case State.HOSTED_ACCOMMODATION:
      case State.HOSTED_ACCOMMODATION_SEND:
      case State.HOSTED_ACCOMMODATION_DELETE:
        return (
          <ProofOfAccommodation
            isDeleting={state.matches(State.HOSTED_ACCOMMODATION_DELETE)}
            isLoading={[
              State.HOSTED_ACCOMMODATION_SEND,
              State.HOSTED_ACCOMMODATION_DELETE,
            ].some(state.matches)}
            isShineStart={moduleData.isShineStart}
            isSubmitting={state.matches(State.HOSTED_ACCOMMODATION_SEND)}
            moduleData={moduleData}
            onContinue={() => send(model.events.NEXT())}
            onDeleteClick={(kycDocumentId) =>
              send(model.events.DELETE_DOCUMENT_FILE(kycDocumentId))
            }
            onPrev={onPrev}
            onSuccess={(document) => {
              send(model.events.SUBMIT_DOCUMENT_FILE(document));
            }}
          />
        );

      case State.POA_TYPE:
      case State.HOSTED_POA_TYPE:
        return (
          <ProofOfAddressSelectType
            isSelfHosted={state.matches(State.POA_TYPE)}
            onOtherTypeClick={() => send(model.events.GO_POA_TYPE_OTHER())}
            onPrev={onPrev}
            onSelectPoaType={(type) => send(model.events.SUBMIT_POA_TYPE(type))}
          />
        );

      case State.POA_UPLOAD:
      case State.HOSTED_POA_UPLOAD:
        if (state.context.poaType === null) {
          throw Error(`poaType shouldn't be null in ${stringifiedState}`);
        }

        return (
          <ProofOfAddressUpload
            isSelfHosted={state.matches(State.POA_UPLOAD)}
            onPrev={onPrev}
            onSuccessfulUpload={(documentFile) =>
              send(model.events.SUBMIT_DOCUMENT_FILE(documentFile))
            }
            poaType={state.context.poaType}
          />
        );

      case State.POA_CHECKLIST:
      case State.POA_SEND:
      case State.HOSTED_POA_CHECKLIST:
      case State.HOSTED_POA_SEND: {
        if (!state.context.documentFile?.url) {
          throw new Error(`Document file should exist in ${stringifiedState}`);
        }

        if (state.context.poaType === null) {
          throw Error(`poaType shouldn't be null in ${stringifiedState}`);
        }
        return (
          <ProofOfAddressChecklist
            address={state.context.address}
            documentFileUrl={state.context.documentFile.url}
            isSaving={[State.POA_SEND, State.HOSTED_POA_SEND].some(
              state.matches,
            )}
            isSelfHosted={[State.POA_CHECKLIST, State.POA_SEND].some(
              state.matches,
            )}
            onContinue={() => send(model.events.NEXT())}
            onPrev={onPrev}
            poaType={state.context.poaType}
            profile={moduleData.viewer.profile}
          />
        );
      }

      case State.POA_REVIEW:
      case State.POA_DELETE:
      case State.HOSTED_POA_REVIEW:
      case State.HOSTED_POA_DELETE:
        return (
          <ReviewAddressAndPoa
            address={state.context.address}
            isLoading={[State.POA_DELETE, State.HOSTED_POA_DELETE].some(
              state.matches,
            )}
            isSelfHosted={[State.POA_REVIEW, State.POA_DELETE].some(
              state.matches,
            )}
            moduleData={moduleData}
            onContinue={() => send(model.events.NEXT())}
            onDeleteClick={(documentId) =>
              send(model.events.DELETE_DOCUMENT_FILE(documentId))
            }
            onEditAddress={() => send(model.events.EDIT_ADDRESS())}
            onPrev={onPrev}
            state={state}
          />
        );

      case State.DONE:
      case State.ABORT:
      case State.ADDRESS_LOAD:
      case State.HOSTED_POA:
      case State.POA:
      case State.NO_DOCUMENT_INIT:
        return <OBLoaderPage onPrev={() => null} />;

      default:
        return assertNever(stringifiedState);
    }
  },
  {
    devTools: true,
    machineConfig,
  },
);

export default ProofOfAddress;
